import { Auth } from "aws-amplify";
import { SignIn as AmplifySignIn } from "aws-amplify-react";
import { Field, Form, Formik, FormikProps, FormikActions } from "formik";
import React, { MouseEvent } from "react";
import * as Yup from "yup";
import { AuthState, IAuthData } from "../../models/auth.models";
import handleEmailValidation from "../utils/emailValidator";
import PasswordValidator from "password-validator";
import { CheckCircle, Cancel } from "@mui/icons-material";

interface ISignInProps {
  authData: IAuthData;
  authState: AuthState;
  onAuthStateChange: (next: AuthState, data: IAuthData) => void;
  onAuthEvent: (next: AuthState, data: IAuthData) => void;
}
export function resetFormError(
  setErrors: (errors: { [key: string]: string }) => void,
) {
  setErrors({}); // Reset errors to an empty object
}
interface ISignInState {
  pending: boolean;
  success: boolean;
  delivery?: { email: string } | null;
}

// Create password validator instance for Yup schema
const passwordValidatorInstance = new PasswordValidator()
  .is()
  .min(8)
  .is()
  .max(20)
  .has()
  .uppercase()
  .has()
  .lowercase()
  .has()
  .digits()
  .has()
  .symbols()
  .has()
  .not()
  .spaces();

const ResetSchema = Yup.object().shape({
  code: Yup.string().required(
    "Please provide the code received in the reset password email",
  ),
  password: Yup.string()
    .required("Please provide the new password")
    .test(
      "password-validation",
      "Password does not meet all requirements. Please check the requirements below.",
      function (value) {
        if (!value) return false;
        return passwordValidatorInstance.validate(value) === true;
      },
    ),
});

class FormikUtils {
  static getFormikFieldClasses(
    formState: FormikProps<IAuthData>, // Formik Props
    fieldName: keyof IAuthData, // Field name from IAuthData
    size?: "sm" | "lg", // Optional size class
  ): string {
    const baseClass = "form-control";
    const sizeClass = size ? `form-control-${size}` : "";
    const validationClass =
      formState.errors[fieldName] && formState.touched[fieldName]
        ? "is-invalid"
        : "";

    return `${baseClass} ${sizeClass} ${validationClass}`.trim();
  }
}

class ResetPassword extends AmplifySignIn<ISignInProps, ISignInState> {
  static defaultProps: ISignInProps = {
    authData: {},
    authState: "signIn",
    onAuthStateChange: () => null,
    onAuthEvent: () => null,
  };

  static state = {
    pending: false,
    delivery: null,
    success: false,
  };

  _validAuthStates = ["resetPassword"];

  // Password validation schema
  private passwordSchema = new PasswordValidator()
    .is()
    .min(8) // Minimum length 8
    .is()
    .max(20) // Maximum length 20
    .has()
    .uppercase() // Must have uppercase letters
    .has()
    .lowercase() // Must have lowercase letters
    .has()
    .digits() // Must have digits
    .has()
    .symbols() // Must have symbols
    .has()
    .not()
    .spaces(); // Should not have spaces

  // Get password validation results
  private getPasswordValidation = (password: string) => {
    const failedRules = this.passwordSchema.validate(password, {
      list: true,
    }) as string[];
    return {
      minLength: !failedRules.includes("min"),
      maxLength: !failedRules.includes("max"),
      hasUppercase: !failedRules.includes("uppercase"),
      hasLowercase: !failedRules.includes("lowercase"),
      hasDigits: !failedRules.includes("digits"),
      hasSymbols: !failedRules.includes("symbols"),
      noSpaces: !failedRules.includes("spaces"),
      isValid: failedRules.length === 0,
    };
  };

  // Render password requirements checklist
  private renderPasswordRequirements = (password: string) => {
    try {
      const validation = this.getPasswordValidation(password || "");
      const requirements = [
        {
          key: "length",
          text: "Contains 8-20 characters",
          valid: validation.minLength && validation.maxLength,
        },
        {
          key: "hasUppercase",
          text: "Contains uppercase letter",
          valid: validation.hasUppercase,
        },
        {
          key: "hasLowercase",
          text: "Contains lowercase letter",
          valid: validation.hasLowercase,
        },
        {
          key: "hasDigits",
          text: "Contains number",
          valid: validation.hasDigits,
        },
        {
          key: "hasSymbols",
          text: "Contains symbol",
          valid: validation.hasSymbols,
        },
        { key: "noSpaces", text: "No spaces", valid: validation.noSpaces },
      ];

      return (
        <div
          style={{
            marginTop: "16px",
            marginBottom: "8px",
            border: "1px solid #e0e0e0",
            padding: "12px",
            borderRadius: "4px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#666",
            }}
          >
            Password Requirements:
          </div>
          {requirements.map((req) => (
            <div
              key={req.key}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              {req.valid ? (
                <CheckCircle
                  style={{
                    color: "#4caf50",
                    fontSize: "16px",
                    marginRight: "8px",
                  }}
                />
              ) : (
                <Cancel
                  style={{
                    color: "#f44336",
                    fontSize: "16px",
                    marginRight: "8px",
                  }}
                />
              )}
              <span
                style={{
                  fontSize: "12px",
                  color: req.valid ? "#4caf50" : "#f44336",
                }}
              >
                {req.text}
              </span>
            </div>
          ))}
        </div>
      );
    } catch (error) {
      console.error("Error rendering password requirements:", error);
      return (
        <div style={{ color: "#f44336", fontSize: "12px" }}>
          Error loading password requirements
        </div>
      );
    }
  };

  onSendRequest = (
    { username = "" }: IAuthData,
    actions: FormikActions<IAuthData>,
  ) => {
    Auth.forgotPassword(username.toLowerCase())
      .then(() => {
        this.setState({ success: true });
      })
      .catch((err) => actions.setStatus({ error: err.message || err }));
  };

  onSubmitNew = (
    { code = "", password = "", username }: IAuthData,
    actions: FormikActions<IAuthData>,
  ) => {
    const { authData = {} } = this.props;
    username = username || authData.username;

    if (!Auth || typeof Auth.forgotPasswordSubmit !== "function") {
      throw new Error(
        "No Auth module found, please ensure @aws-amplify/auth is imported",
      );
    }
    Auth.forgotPasswordSubmit((username || "").toLowerCase(), code, password)
      .then(() => {
        this.changeState("signIn");
        this.setState({ success: true, delivery: null });
      })
      .catch((err) => actions.setStatus({ error: err.message || err }));
  };

  checkContact(user: IAuthData) {
    Auth.verifiedContact(user).then(() => {
      this.changeState("signedIn", user);
    });
  }

  onBackToSignIn = (e: MouseEvent) => {
    e.preventDefault();
    this.changeState("signIn", null);
  };

  validateEmail(value: string) {
    let error;
    const validateAdmin = handleEmailValidation(value);
    if (!value) {
      error = "Please provide your email";
    } else if (validateAdmin == false) {
      error = "Please provide a correct email address";
    }
    return error;
  }

  showComponent() {
    const initialFormValues: IAuthData = {
      username: "",
      code: "",
      password: "",
    };

    return (
      <div className="row mt-4">
        <Formik
          initialValues={initialFormValues}
          onSubmit={this.state.success ? this.onSubmitNew : this.onSendRequest}
          validationSchema={this.state.delivery && ResetSchema}
        >
          {(formState) => (
            <Form
              onChange={() => resetFormError(formState.setErrors)}
              className="col-sm-4 offset-sm-4 mt-4"
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              <div className="card">
                <fieldset
                  className="card-body p-5"
                  disabled={this.state.pending}
                >
                  <h1>Reset your password</h1>
                  {this.state.success && (
                    <p className="text-muted mb-4">
                      Reset code has been sent to the provided email.
                    </p>
                  )}
                  {!this.state.success && (
                    <p className="text-muted mb-4">
                      As part of a system upgrade on Revanista, we need to
                      verify your email address and ask you to create a new
                      password.
                      <p>
                        Enter your email address below to receive a code via
                        email.
                      </p>
                    </p>
                  )}

                  {this.state.success
                    ? this.submitView(formState)
                    : this.sendView(formState)}

                  {formState.status && formState.status.error ? (
                    <p className="text-danger">{formState.status.error}</p>
                  ) : (
                    ""
                  )}

                  <button type="submit" className="btn btn-lg btn-primary mt-4">
                    {this.state.success ? "Reset password" : "Send code"}
                  </button>
                  <p className="text-left mt-2">
                    <a href="#" onClick={this.onBackToSignIn}>
                      Back to Sign In
                    </a>
                  </p>
                  <p className="text-muted mb-4">
                    If you encounter any issue, reach out to{" "}
                    <a href="mailto:support@revanista.com?subject=Revanista Reset Password">
                      support@revanista.com
                    </a>
                  </p>
                </fieldset>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }

  private sendView = (formState: FormikProps<IAuthData>) => (
    <div className="form-group">
      <label htmlFor="email" className="required">
        Email
      </label>
      <Field
        type="text"
        name="username"
        id="email"
        className={FormikUtils.getFormikFieldClasses(
          formState,
          "username",
          "lg",
        )}
        placeholder="Enter your email"
        validate={this.validateEmail}
      />
      <div className="invalid-feedback">{formState.errors.username}</div>
    </div>
  );

  private submitView = (formState: FormikProps<IAuthData>) => (
    <>
      <div className="form-group">
        Code:
        <Field
          autoComplete="off"
          type="text"
          name="code"
          className={FormikUtils.getFormikFieldClasses(formState, "code", "lg")}
          placeholder="Code"
        />
        <div className="invalid-feedback">{formState.errors.code}</div>
      </div>

      <div className="form-group">
        New password :
        <Field
          autoComplete="off"
          type="password"
          name="password"
          className={FormikUtils.getFormikFieldClasses(
            formState,
            "password",
            "lg",
          )}
          //placeholder="New Password"
        />
        <div className="invalid-feedback">{formState.errors.password}</div>
        {/* Password Requirements Checklist */}
        {this.renderPasswordRequirements(formState.values.password || "")}
      </div>
    </>
  );
}

export { ResetPassword as ResetPassword };
